import React, { useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, getDocs, doc, setDoc, deleteDoc, query, orderBy, serverTimestamp } from 'firebase/firestore';
import Swal from 'sweetalert2';
import { useSelectedLocation } from './LocationContext';
import SetupGuard from '../components/SetupGuard';
import AdminLayout from './AdminLayout';

const SHIFT_COLORS = [
  '#ff6b35', '#28a745', '#007bff', '#6f42c1', '#17a2b8', '#dc3545', '#ffc107', '#20c997',
];

const ShiftsPage = ({ embedded = false }) => {
  const [user, setUser] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [formData, setFormData] = useState({
    shiftName: '',
    startHour: '09:00',
    endHour: '17:00',
    endsNextDay: false,
    color: '#007bff',
    notes: '',
    locationId: '',
  });

  const auth = getAuth();
  const { selectedLocation } = useSelectedLocation() || {};

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await loadShifts(currentUser.uid);
        await loadLocations(currentUser.uid);
      }
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadShifts = async (uid) => {
    try {
      const db = getFirestore();
      const snapshot = await getDocs(
        query(collection(db, 'users', uid, 'shifts'), orderBy('shiftName'))
      );
      setShifts(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('Error loading shifts:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadLocations = async (uid) => {
    try {
      const db = getFirestore();
      const snapshot = await getDocs(collection(db, 'users', uid, 'locations'));
      setLocations(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('Error loading locations:', e);
    }
  };

  const getLocationName = (id) => {
    const loc = locations.find(l => l.id === id);
    return loc ? loc.companyName || loc.locationName : '—';
  };

  // Filter shifts by selected location from navbar
  const filteredShifts = selectedLocation?.id
    ? shifts.filter(s => s.locationId === selectedLocation.id)
    : shifts;

  const validateForm = () => {
    if (!formData.shiftName.trim()) {
      Swal.fire('Error', 'Shift name is required', 'error');
      return false;
    }
    if (!formData.startHour || !formData.endHour) {
      Swal.fire('Error', 'Start and end hours are required', 'error');
      return false;
    }
    if (!formData.locationId) {
      Swal.fire('Error', 'Location is required — shifts are scheduled per location', 'error');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm() || !user) return;

    try {
      const db = getFirestore();
      const data = {
        shiftName: formData.shiftName.trim(),
        startHour: formData.startHour,
        endHour: formData.endHour,
        endsNextDay: formData.endsNextDay,
        color: formData.color,
        notes: formData.notes.trim() || null,
        locationId: formData.locationId || null,
        updatedAt: serverTimestamp(),
      };

      if (editingShift) {
        await setDoc(doc(db, 'users', user.uid, 'shifts', editingShift.id), data, { merge: true });
        Swal.fire('Success', 'Shift updated!', 'success');
      } else {
        data.createdAt = serverTimestamp();
        const docRef = doc(collection(db, 'users', user.uid, 'shifts'));
        await setDoc(docRef, data);
        Swal.fire('Success', 'Shift created!', 'success');
      }

      setShowModal(false);
      setEditingShift(null);
      setFormData({ shiftName: '', startHour: '09:00', endHour: '17:00', endsNextDay: false, color: '#007bff', notes: '', locationId: '' });
      await loadShifts(user.uid);
      window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
    } catch (e) {
      console.error('Error saving shift:', e);
      Swal.fire('Error', e.message || 'Failed to save shift', 'error');
    }
  };

  const handleDelete = async (shift) => {
    const result = await Swal.fire({
      title: 'Delete Shift?',
      text: `Are you sure you want to delete "${shift.shiftName}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Delete',
    });
    if (!result.isConfirmed || !user) return;

    try {
      const db = getFirestore();
      await deleteDoc(doc(db, 'users', user.uid, 'shifts', shift.id));
      Swal.fire('Deleted!', 'Shift has been deleted.', 'success');
      await loadShifts(user.uid);
      window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
    } catch (e) {
      console.error('Error deleting shift:', e);
      Swal.fire('Error', e.message || 'Failed to delete shift', 'error');
    }
  };

  const openNew = () => {
    setEditingShift(null);
    setFormData({
      shiftName: '',
      startHour: '09:00',
      endHour: '17:00',
      endsNextDay: false,
      color: '#007bff',
      notes: '',
      locationId: selectedLocation?.id || (locations.length > 0 ? locations[0].id : ''),
    });
    setShowModal(true);
  };

  const openEdit = (shift) => {
    setEditingShift(shift);
    setFormData({
      shiftName: shift.shiftName || '',
      startHour: shift.startHour || '09:00',
      endHour: shift.endHour || '17:00',
      endsNextDay: shift.endsNextDay || false,
      color: shift.color || '#007bff',
      notes: shift.notes || '',
      locationId: shift.locationId || '',
    });
    setShowModal(true);
  };

  const toAmPm = (timeStr) => {
    if (!timeStr) return '';
    const [h, m] = timeStr.split(':').map(Number);
    const suffix = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${m.toString().padStart(2, '0')} ${suffix}`;
  };

  const formatDuration = (start, end, nextDay) => {
    if (!start || !end) return '—';
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    let diffMinutes = (eh * 60 + em) - (sh * 60 + sm);
    if (nextDay) diffMinutes += 24 * 60;
    const h = Math.floor(diffMinutes / 60);
    const m = diffMinutes % 60;
    return `${h}h${m > 0 ? ` ${m}m` : ''}`;
  };

  if (loading) {
    const loader = (
      <div className="text-center py-5">
        <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 80, height: 80, objectFit: 'contain' }} />
      </div>
    );
    if (embedded) return loader;
    return loader;
  }

  const content = (
    <SetupGuard prerequisites={['location']}>
      <>
      <div className="shifts-page">
        {/* Header */}
        <div className="row mb-4 align-items-center">
          <div className="col-md-6">
            <h4 className="font-weight-bold">Shifts</h4>
            <p className="text-muted">
              {selectedLocation?.id ? (
                <>
                  <span className="badge px-2 py-1" style={{ background: '#ff6b3520', color: '#ff6b35', borderRadius: 8 }}>
                    <i className="fas fa-store mr-1"></i>
                    {selectedLocation.companyName || selectedLocation.locationName}
                  </span>
                  <span className="ml-2">Showing shifts for this location</span>
                </>
              ) : (
                'Create and manage work shifts for scheduling'
              )}
            </p>
          </div>
          <div className="col-md-6 text-md-right">
            <button className="btn btn-primary" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }} onClick={openNew}>
              <i className="fas fa-plus mr-2"></i>Add Shift
            </button>
          </div>
        </div>

        {/* Shifts Cards */}
        {filteredShifts.length === 0 ? (
          <div className="text-center py-5">
            <i className="fas fa-clock fa-4x text-muted mb-4"></i>
            <h4 className="text-muted">
              {selectedLocation?.id ? `No shifts at ${selectedLocation.companyName || selectedLocation.locationName}` : 'No shifts yet'}
            </h4>
            <p className="text-muted">Create shifts like Morning, Evening, or Night to assign to employees</p>
          </div>
        ) : (
          <div className="row">
            {filteredShifts.map(shift => (
              <div key={shift.id} className="col-md-6 col-lg-4 mb-4">
                <div className="card h-100" style={{ borderRadius: '16px', border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
                  <div className="card-body">
                    <div className="d-flex align-items-center mb-3">
                      <div
                        className="d-flex align-items-center justify-content-center mr-3"
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 12,
                          backgroundColor: (shift.color || '#007bff') + '20',
                        }}
                      >
                        <i className="fas fa-clock" style={{ color: shift.color || '#007bff', fontSize: 18 }}></i>
                      </div>
                      <div>
                        <h5 className="font-weight-bold m-0">{shift.shiftName}</h5>
                        <small className="text-muted">{formatDuration(shift.startHour, shift.endHour, shift.endsNextDay)}</small>
                      </div>
                    </div>

                    <div className="mb-3">
                      <div className="d-flex align-items-center mb-2">
                        <i className="fas fa-play-circle text-success mr-2" style={{ width: 20 }}></i>
                        <span>{toAmPm(shift.startHour)}</span>
                        {shift.endsNextDay && <span className="badge badge-warning ml-2" style={{ fontSize: '0.7rem' }}>next day</span>}
                      </div>
                      <div className="d-flex align-items-center">
                        <i className="fas fa-stop-circle text-danger mr-2" style={{ width: 20 }}></i>
                        <span>{toAmPm(shift.endHour)}</span>
                      </div>
                    </div>

                    <div className="mb-2">
                      <small className="text-muted">
                        <i className="fas fa-store mr-1 text-primary"></i>
                        {getLocationName(shift.locationId)}
                      </small>
                    </div>

                    {shift.notes && (
                      <p className="text-muted small mb-3" style={{ fontSize: '0.85rem' }}>{shift.notes}</p>
                    )}

                    <div className="text-right">
                      <button className="btn btn-sm btn-outline-primary mr-1" style={{ borderRadius: 8 }} onClick={() => openEdit(shift)}>
                        <i className="fas fa-edit"></i>
                      </button>
                      <button className="btn btn-sm btn-outline-danger" style={{ borderRadius: 8 }} onClick={() => handleDelete(shift)}>
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content" style={{ borderRadius: 16 }}>
                <div className="modal-header border-0">
                  <h5 className="modal-title font-weight-bold">{editingShift ? 'Edit Shift' : 'Add Shift'}</h5>
                  <button type="button" className="close" onClick={() => setShowModal(false)}>
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Location <span className="text-danger">*</span></label>
                    <select
                      className="form-control"
                      value={formData.locationId}
                      onChange={e => setFormData({ ...formData, locationId: e.target.value })}
                    >
                      <option value="">Select Location</option>
                      {locations.map(loc => (
                        <option key={loc.id} value={loc.id}>{loc.companyName || loc.locationName}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Shift Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.shiftName}
                      onChange={e => setFormData({ ...formData, shiftName: e.target.value })}
                      placeholder="e.g. Evening Shift"
                    />
                  </div>
                  <div className="row">
                    <div className="col-6">
                      <div className="form-group">
                        <label>Start Time <span className="text-danger">*</span></label>
                        <input
                          type="time"
                          className="form-control"
                          value={formData.startHour}
                          onChange={e => setFormData({ ...formData, startHour: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>End Time <span className="text-danger">*</span></label>
                        <input
                          type="time"
                          className="form-control"
                          value={formData.endHour}
                          onChange={e => setFormData({ ...formData, endHour: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    <div className="custom-control custom-checkbox">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id="endsNextDay"
                        checked={formData.endsNextDay}
                        onChange={e => setFormData({ ...formData, endsNextDay: e.target.checked })}
                      />
                      <label className="custom-control-label" htmlFor="endsNextDay">
                        Ends next day (e.g. 6pm to 2am)
                      </label>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Color</label>
                    <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                      {SHIFT_COLORS.map(color => (
                        <button
                          key={color}
                          className="btn p-0"
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 8,
                            backgroundColor: color,
                            border: formData.color === color ? '3px solid #333' : '2px solid transparent',
                          }}
                          onClick={() => setFormData({ ...formData, color })}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Notes</label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={formData.notes}
                      onChange={e => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Optional notes about this shift"
                    />
                  </div>
                </div>
                <div className="modal-footer border-0">
                  <button className="btn btn-outline-secondary" style={{ borderRadius: 8 }} onClick={() => setShowModal(false)}>Cancel</button>
                  <button className="btn btn-primary" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35', borderRadius: 8 }} onClick={handleSave}>
                    {editingShift ? 'Update' : 'Create'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
    </SetupGuard>
  );

  if (embedded) return content;
  return (
    <AdminLayout title="Shifts">
      {content}
    </AdminLayout>
  );
};

export default ShiftsPage;
